import { Guardian, GuardianQuery, GuardianRelation } from '@dreryk/egabinet-patient-frontend-api';

import { CqrsQueryPerformResult, performCqrsQuerySearch } from '@dreryk/command-query-react-common';
import { createTranslatedText } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { referralToPsychiatricHospitalTranslationKeys } from '../../../../../translations';
import { GuardianFieldName, GuardianSelectProps } from '../guardianSection.types';
import { mapGuardianToReferralToPsychiatricHospitalFormGuardian } from '../guardianSection.utils';

import { ReferralToPsychiatricHospitalFormGuardianSelectBean } from './guardianSelect.types';

const guardiansSearchCreator = (patientId: number, relation?: GuardianRelation, statutoryRepresentative?: boolean) => {
   return () => async (): CqrsQueryPerformResult<Guardian[]> =>
      performCqrsQuerySearch<Guardian>(() => {
         const guardianQuery = new GuardianQuery();
         guardianQuery.patientId = Slot.emptyIfNull(patientId);
         guardianQuery.relation = Slot.emptyIfNull(relation || null);
         guardianQuery.statuatoryRepresentative = Slot.emptyIfNull(statutoryRepresentative || null);
         return guardianQuery;
      });
};

const getRelationByFieldName = (fieldName: GuardianFieldName): GuardianRelation | undefined =>
   fieldName === 'actualGuardian' ? 'ACTUAL_GUARDIAN' : undefined;

const isStatutoryRepresentative = (fieldName: GuardianFieldName): boolean => fieldName === 'statutoryRepresentative';

const getGuardianDisplayValue = (guardian: ReferralToPsychiatricHospitalFormGuardianSelectBean): string => {
   if (guardian === 'ENTERED_BY_USER') {
      return '';
   }
   return `${guardian.firstName} ${guardian.lastName}`;
};

const triggerLabelExtractor = (item: ReferralToPsychiatricHospitalFormGuardianSelectBean): string => {
   switch (item) {
      case 'ENTERED_BY_USER':
         return '-';
      default:
         return getGuardianDisplayValue(item);
   }
};

export const GuardianSelect = <FIELD_NAME extends GuardianFieldName>(
   props: GuardianSelectProps<FIELD_NAME>
): JSX.Element => {
   return (
      <Select<ReferralToPsychiatricHospitalFormGuardianSelectBean>
         testId={props.testId}
         triggerLabelExtractor={triggerLabelExtractor}
         controller={props.register(props.name, { required: !props.disabled })}
         label={
            props.name === 'actualGuardian'
               ? referralToPsychiatricHospitalTranslationKeys.actualGuardian
               : referralToPsychiatricHospitalTranslationKeys.statutoryRepresentative
         }
         placeholder={referralToPsychiatricHospitalTranslationKeys.choose}
         disabled={props.disabled}
      >
         <Select.SearchCreator<Guardian>
            searchCreator={guardiansSearchCreator(
               props.patientId,
               getRelationByFieldName(props.name),
               isStatutoryRepresentative(props.name)
            )}
         >
            {({ items }) => (
               <Select.List<Guardian> items={items} keyExtractor={'id'}>
                  {(guardian) => (
                     <Select.Item<ReferralToPsychiatricHospitalFormGuardianSelectBean>
                        testId={guardian.id}
                        item={mapGuardianToReferralToPsychiatricHospitalFormGuardian(guardian)}
                        label={createTranslatedText(
                           getGuardianDisplayValue(mapGuardianToReferralToPsychiatricHospitalFormGuardian(guardian))
                        )}
                     />
                  )}
               </Select.List>
            )}
         </Select.SearchCreator>
      </Select>
   );
};
